import React, { useState } from "react";
import axios from "axios";
import { useSnackbar } from "notistack";
import { FaWhatsapp, FaTimes, FaShieldAlt } from "react-icons/fa";
import { useLanguage } from "../context/LanguageContext";

const COUNTRIES = [
  { code: "55", name: "Brasil", flag: "🇧🇷", placeholder: "(11) 99999-9999" },
  { code: "1", name: "Estados Unidos", flag: "🇺🇸", placeholder: "(555) 000-0000" },
  { code: "351", name: "Portugal", flag: "🇵🇹", placeholder: "912 345 678" },
  { code: "54", name: "Argentina", flag: "🇦🇷", placeholder: "9 11 1234-5678" },
  { code: "34", name: "Espanha", flag: "🇪🇸", placeholder: "612 34 56 78" },
  { code: "39", name: "Itália", flag: "🇮🇹", placeholder: "312 345 6789" },
  { code: "33", name: "França", flag: "🇫🇷", placeholder: "6 12 34 56 78" },
  { code: "49", name: "Alemanha", flag: "🇩🇪", placeholder: "151 12345678" },
  { code: "1", name: "Canadá", flag: "🇨🇦", placeholder: "(555) 000-0000" },
  { code: "598", name: "Uruguai", flag: "🇺🇾", placeholder: "99 123 456" },
  { code: "56", name: "Chile", flag: "🇨🇱", placeholder: "9 1234 5678" },
  { code: "595", name: "Paraguai", flag: "🇵🇾", placeholder: "981 123 456" },
  { code: "44", name: "Reino Unido", flag: "🇬🇧", placeholder: "7123 456789" },
  { code: "81", name: "Japão", flag: "🇯🇵", placeholder: "90-1234-5678" },
];

const ModalCapturaCelular = ({ isOpen, onClose, onSuccess, onSkip, userId }) => {
  const { t } = useLanguage();
  const [selectedDDI, setSelectedDDI] = useState("55");
  const [phoneRaw, setPhoneRaw] = useState("");
  const [phoneFormatted, setPhoneFormatted] = useState("");
  const [loading, setLoading] = useState(false);
  const [validationError, setValidationError] = useState("");
  const { enqueueSnackbar } = useSnackbar();

  if (!isOpen) return null;

  const currentCountry =
    COUNTRIES.find((c) => c.code === selectedDDI) || COUNTRIES[0];

  // Máscara e consistência para celular brasileiro
  const formatBrazilianPhone = (digits) => {
    const d = digits.slice(0, 11); // máx 11 dígitos
    if (d.length <= 2) return d ? `(${d}` : "";
    if (d.length <= 7) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
    return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
  };

  const handlePhoneChange = (e) => {
    const rawDigits = e.target.value.replace(/\D/g, "");
    setValidationError("");

    if (selectedDDI === "55") {
      const limitedDigits = rawDigits.slice(0, 11);
      setPhoneRaw(limitedDigits);
      setPhoneFormatted(formatBrazilianPhone(limitedDigits));
    } else {
      const limitedDigits = rawDigits.slice(0, 14);
      setPhoneRaw(limitedDigits);
      setPhoneFormatted(limitedDigits);
    }
  };

  const handleCountryChange = (e) => {
    const newDDI = e.target.value;
    setSelectedDDI(newDDI);
    setPhoneRaw("");
    setPhoneFormatted("");
    setValidationError("");
  };

  const validatePhone = () => {
    if (!phoneRaw) {
      setValidationError("Por favor, digite o número do celular.");
      return false;
    }

    if (selectedDDI === "55") {
      // Regras de celular no Brasil:
      // 11 dígitos no total (2 dígitos de DDD + 9 dígitos de celular)
      if (phoneRaw.length !== 11) {
        setValidationError(
          "Celular brasileiro deve ter 11 dígitos: DDD (2) + Número (9)."
        );
        return false;
      }
      const ddd = parseInt(phoneRaw.slice(0, 2), 10);
      if (ddd < 11 || ddd > 99) {
        setValidationError("DDD inválido.");
        return false;
      }
      // O primeiro dígito após o DDD deve ser 9 para ser celular
      if (phoneRaw[2] !== "9") {
        setValidationError(
          "Número de celular inválido. No Brasil, celulares devem começar com 9 após o DDD."
        );
        return false;
      }
    } else {
      if (phoneRaw.length < 8) {
        setValidationError("Número internacional muito curto.");
        return false;
      }
    }

    return true;
  };

  const handleSalvar = async (e) => {
    e.preventDefault();
    if (!validatePhone()) return;

    // Formato padrão WhatsApp E.164 limpo: DDI + DDD + Número (ex: 5511987654321)
    const fullWhatsAppNumber = `${selectedDDI}${phoneRaw}`;

    setLoading(true);
    try {
      const token =
        localStorage.getItem("tokenAcesso") || localStorage.getItem("token");
      const cleanToken = token ? token.replace(/^["']|["']$/g, "").trim() : "";

      const headers = cleanToken
        ? { Authorization: `Bearer ${cleanToken}` }
        : {};

      await axios.patch(
        `${import.meta.env.VITE_REACT_APP_BACKEND_BASEURL}/atletas/${userId}/celular`,
        { celular: fullWhatsAppNumber },
        { headers }
      );

      // Atualiza o objeto do usuário salvo no localStorage
      const storedUser = JSON.parse(localStorage.getItem("user")) || {};
      storedUser.celular = fullWhatsAppNumber;
      localStorage.setItem("user", JSON.stringify(storedUser));

      enqueueSnackbar("WhatsApp cadastrado com sucesso!", {
        variant: "success",
      });

      if (onSuccess) {
        onSuccess(fullWhatsAppNumber);
      }
    } catch (error) {
      console.error("Erro ao atualizar celular:", error);
      enqueueSnackbar(
        "Não foi possível salvar o celular no momento, mas você pode continuar com o pagamento.",
        { variant: "warning" }
      );
      // Prossegue mesmo em caso de falha de rede
      if (onSkip) {
        onSkip();
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in">
      <div className="bg-base-100 rounded-2xl shadow-2xl max-w-md w-full p-6 border border-base-300 relative">
        {/* Botão fechar */}
        <button
          type="button"
          onClick={onSkip}
          className="btn btn-sm btn-circle btn-ghost absolute right-3 top-3 text-base-content/70 hover:text-base-content"
          title="Fechar"
        >
          <FaTimes />
        </button>

        {/* Cabeçalho */}
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-full bg-green-500/10 flex items-center justify-center text-green-600 text-2xl shrink-0">
            <FaWhatsapp />
          </div>
          <div>
            <h3 className="text-xl font-bold text-base-content leading-tight">
              {t("phoneModal.title")}
            </h3>
          </div>
        </div>

        {/* Mensagem explicativa persuasiva */}
        <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-3 mb-5 text-sm text-base-content/85">
          <p className="font-semibold text-blue-600 dark:text-blue-400 mb-1">
            {t("phoneModal.approvalNotice")}
          </p>
          <p className="text-xs leading-relaxed">
            {t("phoneModal.approvalDescription")}
          </p>
        </div>

        {/* Formulário de captura */}
        <form onSubmit={handleSalvar} className="space-y-4">
          <div>
   
            <div className="flex gap-2">
              {/* Seletor de DDI com bandeira */}
              <div className="relative w-28 sm:w-32 shrink-0">
                <select
                  value={selectedDDI}
                  onChange={handleCountryChange}
                  className="select select-bordered font-semibold text-sm px-2.5 w-full h-12 min-h-12 bg-base-200 cursor-pointer"
                  title="Selecione o país e DDI"
                >
                  {COUNTRIES.map((country, idx) => (
                    <option key={`${country.code}-${idx}`} value={country.code}>
                      {country.flag} +{country.code}
                    </option>
                  ))}
                </select>
              </div>

              {/* Input com máscara */}
              <div className="relative flex-1">
                <input
                  type="tel"
                  value={phoneFormatted}
                  onChange={handlePhoneChange}
                  placeholder={currentCountry.placeholder}
                  className={`input input-bordered w-full h-12 text-base font-medium ${
                    validationError ? "input-error" : ""
                  }`}
                  autoFocus
                />
              </div>
            </div>

            {/* Mensagem de erro de validação */}
            {validationError && (
              <p className="text-xs text-error mt-1.5 font-medium">
                {validationError}
              </p>
            )}

            <p className="text-[11px] text-base-content/50 mt-1">
              {selectedDDI === "55"
                ? "DDD + 9 dígitos (ex: 11 98765-4321)"
                : "Digite apenas os dígitos do seu celular com o código de área"}
            </p>
          </div>

          {/* Botões de Ação */}
          <div className="pt-2 flex flex-col gap-2">
            <button
              type="submit"
              disabled={loading}
              className="btn btn-primary bg-green-600 hover:bg-green-700 active:bg-green-800 text-white border-none w-full h-12 font-semibold shadow-md gap-2"
            >
              {loading ? (
                <>
                  <span className="loading loading-spinner loading-sm"></span>
                  <span>{t("phoneModal.saving")}</span>
                </>
              ) : (
                <>
                  <FaWhatsapp className="text-lg" />
                  <span>{t("phoneModal.save")}</span>
                </>
              )}
            </button>

            <button
              type="button"
              onClick={onSkip}
              disabled={loading}
              className="btn btn-ghost btn-sm text-base-content/70 hover:text-base-content font-normal"
            >
              {t("phoneModal.later")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ModalCapturaCelular;
